import { ReactNode, useId } from "react";
import { Box, Skeleton, Switch, SwitchProps, Tooltip, Typography, TypographyProps } from "@mui/material";
import i18nPackage from "../../i18n/i18n";
import styles from "./index.module.css";

// None = no text, left = text appears to the left, dual = label off to the left of switch, label on to the right of switch, 
// right = text appears to the right of switch. Default: `none`
type CoreSwitchLabelType = "none" | "left" | "dual" | "right";

export type CoreSwitchProps = Omit<SwitchProps, "checked"> & {
	width?: string;
	height?: string;
	checked: boolean;
	/**Text to show when toggle is on */
	labelOnText?: string;
	/**Text to show when toggle is off */
	labelOffText?: string;
	/**None = no text, left = text appears to the left, dual = label off to the left of switch, label on to the right of switch, 
	 * right = text appears to the right of switch. Default: `none` */
	labelTextType?: CoreSwitchLabelType;
	disabledTooltip?: ReactNode;
	ariaLabelledBy?: string;
	slotProps?: {
		switchLabel?: Omit<TypographyProps, "children" | "id">;
	};
};

export function CoreSwitch({
	width = "40px",
	height = "24px",
	checked,
	labelOnText = i18nPackage.t("coreComponents.coreSwitch.enabled"),
	labelOffText = i18nPackage.t("coreComponents.coreSwitch.disabled"),
	labelTextType = "none",
	disabled,
	disabledTooltip,
	sx,
	ariaLabelledBy,
	slotProps,
	...props
}: CoreSwitchProps) {
	const disabledTooltipId = useId();
	const leftLabelId = useId();
	const rightLabelId = useId();

	const fallbackId = checked && labelTextType === "dual" ? rightLabelId : labelTextType === "right" ? rightLabelId : leftLabelId;
	const ariaLabelledByFinal = ariaLabelledBy ?? props.inputProps?.["aria-labelledby"] ?? fallbackId;

	let switchElement = (
		<Switch
			{...props}
			inputProps={{
				...props.inputProps,
				"aria-labelledby": ariaLabelledByFinal,
			}}
			disabled={disabled}
			sx={{
				width,
				height,
				padding: "2px",
				"&.Mui-focusVisible": {
					borderRadius: `calc(${height} / 2)`,
				},
				"& .MuiSwitch-switchBase.Mui-checked": {
					transform: `translateX(calc(${width} - ${height}))`,
				},
				"& .MuiSwitch-thumb": {
					width: `calc(${height} - 8px)`,
					height: `calc(${height} - 8px)`,
					borderRadius: "50%",
				},
				"& .MuiSwitch-track": {
					height: `calc(${height} - 4px)`,
					margin: "auto 0",
					borderRadius: `calc((${height} - 4px) / 2)`,
				},
				...sx,
			}}
			checked={checked}
		/>
	);

	if (disabled && disabledTooltip) {
		switchElement = (
			<Tooltip id={disabledTooltipId} title={disabledTooltip}>
				<Box
					aria-label={undefined}
					aria-describedby={disabledTooltipId}
					tabIndex={0}
					sx={{
						borderRadius: `calc(${height} / 2)`,
						"&:focus-visible": {
							outline: "2px solid var(--focus-outline-color-dark)",
							outlineOffset: "2px",
						},
					}}
				>
					{switchElement}
				</Box>
			</Tooltip>
		);
	}

	if (labelTextType === "none") return switchElement;

	return (
		<div className={styles.switchRow}>
			{/* LEFT LABEL: Renders for "left" or "dual" */}
			{(labelTextType === "left" || labelTextType === "dual") && (
				<Typography
					{...slotProps?.switchLabel}
					id={leftLabelId}
					sx={{
						color: disabled ? "#767676" : "#262626",
						...slotProps?.switchLabel?.sx,
					}}
					className={`${styles.switchLabel} ${slotProps?.switchLabel?.className || ""}`}
				>
					{checked && labelTextType !== "dual" ? labelOnText : labelOffText}
				</Typography>
			)}
			
			{switchElement}
			
			{/* RIGHT LABEL: Renders for "right" or "dual" */}
			{(labelTextType === "right" || labelTextType === "dual") && (
				<Typography
					{...slotProps?.switchLabel}
					id={rightLabelId}
					sx={{
						color: disabled ? "#767676" : "#262626",
						...slotProps?.switchLabel?.sx,
					}}
					className={`${styles.switchLabel} ${slotProps?.switchLabel?.className || ""}`}
				>
					{labelTextType === "dual" || checked ? labelOnText : labelOffText}
				</Typography>
			)}
		</div>
	);
}

export function CoreSwitchSkeleton({
	width = "40px",
	height = "24px",
	labelTextType = "none",
}: Pick<CoreSwitchProps, "width" | "height" | "labelTextType">) {
	const switchSkeleton = <Skeleton variant="rounded" width={width} height={height} sx={{ borderRadius: 25 }} />;

	if (labelTextType === "none") {
		return switchSkeleton;
	}

	return (
		<div style={{ display: "flex", alignItems: "center" }}>
			<Skeleton
				variant="text"
				width={50}
				sx={{
					marginRight: "8px",
					marginTop: "1px",
					alignSelf: "center",
				}}
			/>
			{switchSkeleton}
		</div>
	);
}
